import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PjSandboxHistoryEntry } from '@cortex/domain-product-innovation-lab-types';

/** EditHistoryPanel の props。 */
export interface EditHistoryPanelProps {
  /** 表示する編集履歴（新しい順）。 */
  entries: PjSandboxHistoryEntry[];
  /** 履歴を読み込み中か。 */
  loading: boolean;
  /** 履歴の取得に失敗したか。 */
  failed: boolean;
  /** 表示件数より古い履歴が残っているか（「もっと見る」の出し分け）。 */
  hasMore: boolean;
  /** 「もっと見る」で表示件数を増やす。 */
  onShowMore: () => void;
}

/**
 * 実行時刻を「今日 14:32 / 昨日 11:05 / 8/29 09:12」の粒度に整える。
 * 直近の操作ほど「いつ」が読み取りやすい形にするための表示専用ロジック。
 * @graph-connects none
 */
function formatAt(iso: string, now: Date, t: (key: string) => string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  const time = `${at.getHours().toString().padStart(2, '0')}:${at
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;

  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(at)) / 86_400_000);

  if (dayDiff === 0) return `${t('pjSandbox.history.today')} ${time}`;
  if (dayDiff === 1) return `${t('pjSandbox.history.yesterday')} ${time}`;
  return `${(at.getMonth() + 1).toString()}/${at.getDate().toString()} ${time}`;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板の編集履歴ブロック。白板グリッドの下に置き、「誰が・いつ・何をしたか」を新しい順に並べる。操作ラベルは locale 非依存で保存された action と before/after トークンから i18n テンプレートで組み立てる。折りたたみ可能で、既定は開いた状態
 * @graph-connects none
 */
export function EditHistoryPanel({
  entries,
  loading,
  failed,
  hasMore,
  onShowMore,
}: EditHistoryPanelProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  // 相対表記（今日 / 昨日）の基準時刻。描画のたびに now を作ると差分レンダリングのたびに
  // 表示が揺れうるため、マウント時点で固定する。
  const now = useMemo(() => new Date(), []);

  const latest = entries[0];

  return (
    <section className="shrink-0 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('pjSandbox.history.title')}
        </h3>
        {latest !== undefined && collapsed && (
          <span className="truncate text-[11px] text-[var(--color-text-muted)]">
            {t('pjSandbox.history.latestSummary', {
              actor: latest.actorName,
              at: formatAt(latest.at, now, t),
            })}
          </span>
        )}
        <button
          type="button"
          onClick={() => {
            setCollapsed((prev) => !prev);
          }}
          aria-expanded={!collapsed}
          className="ml-auto shrink-0 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)]"
        >
          {collapsed ? t('pjSandbox.history.expand') : t('pjSandbox.history.collapse')}
        </button>
      </div>

      {!collapsed && (
        <div className="max-h-44 overflow-y-auto px-3 py-2">
          {failed ? (
            <p className="py-3 text-center text-xs text-[var(--color-error)]">
              {t('pjSandbox.history.error')}
            </p>
          ) : loading && entries.length === 0 ? (
            <p className="py-3 text-center text-xs text-[var(--color-text-muted)]">
              {t('pjSandbox.history.loading')}
            </p>
          ) : entries.length === 0 ? (
            <p className="py-3 text-center text-xs text-[var(--color-text-muted)]">
              {t('pjSandbox.history.empty')}
            </p>
          ) : (
            <>
              <ul className="flex flex-col">
                {entries.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-[var(--color-border)] py-1.5 last:border-b-0"
                  >
                    <span className="w-20 shrink-0 truncate text-xs font-semibold text-[var(--color-text-primary)]">
                      {entry.actorName}
                    </span>
                    <span className="w-20 shrink-0 text-[11px] tabular-nums text-[var(--color-text-muted)]">
                      {formatAt(entry.at, now, t)}
                    </span>
                    <span className="min-w-0 flex-1 text-xs text-[var(--color-text-secondary)]">
                      {t(`pjSandbox.history.action.${entry.action}`, {
                        target: entry.targetName,
                        before: entry.before ?? '',
                        after: entry.after ?? '',
                        count: entry.count ?? 0,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
              {hasMore && (
                <div className="pt-2 text-center">
                  <button
                    type="button"
                    onClick={onShowMore}
                    disabled={loading}
                    className="rounded-md border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? t('pjSandbox.history.loading') : t('pjSandbox.history.showMore')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
